import { useIntl } from "react-intl";
import { Link } from "react-router";

import type { SearchArticlesPayload } from "~/shop/search-articles.server";

import { useURL } from "~/contexts";

import messages from "./search-tab-messages";

/**
 * Articles results list for the search Articles tab (SFCC-11499).
 *
 * Each tile links to the CIO-returned article URL AS-IS — FSA resolves to the
 * SFRA-proxied `/learning-center/...` path, HSA to the Workers
 * `/learning-center/articles/*` route. We do NOT assemble slugs. URLs are
 * routed through `useURL()` so a site-relative path is host-resolved the same
 * way the rest of the app links (an absolute URL passes through unchanged).
 */
export function SearchArticlesTab({
  articles,
}: {
  articles: SearchArticlesPayload;
}) {
  const url = useURL();
  const intl = useIntl();

  if (articles.items.length === 0) {
    return (
      <p
        data-testid="search-articles-empty"
        className="px-2 py-8 font-grotesque text-sm text-blue-04 sm:px-6"
      >
        {intl.formatMessage(messages.noArticles)}
      </p>
    );
  }

  return (
    <ul
      data-testid="search-articles-list"
      className="flex w-full flex-col divide-y divide-gray-100 px-2 sm:px-6"
    >
      {articles.items.map(article => (
        <li key={article.id}>
          <Link
            to={url(article.url)}
            data-testid="search-article-link"
            className="block py-4 font-grotesque text-base font-medium text-blue-04 hover:text-accent hover:underline"
          >
            {article.title}
          </Link>
        </li>
      ))}
    </ul>
  );
}
